import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  formatChartAxisLabel,
  getChartGroupSortKey,
} from 'i18n/calendarLabels';
import {
  getOrgMappedReturns,
  getOrgOrganisations,
  getOrgLoading,
  getReceiptStatisticsAll,
} from 'store/organisation/selectors';
import { useReceiptStatisticsAll } from 'utils/useReceiptStatisticsAll';
import { getDashboardReceiptStatistics } from 'utils/receipt-statistics';
import { getAuthUser } from 'store/auth/selectors';
import { getUserMembers } from 'store/user/selectors';
import { DataLogStatus, DrawerType } from 'store/app/enums';
import { DateOptions } from 'store/api/enums';
import { turnDrawerOn } from 'store/app/actions';
import { DrawerData } from 'store/app/types';
import { VatOverviewPage } from 'pages/vat';
import routes from 'routes';
import { reportViewPath } from 'utils/tifd-mapping';
import { isPwaTaxReturnCreationAllowed } from 'utils/pwa-nav';
import {
  Preference,
  PreferenceActions,
  usePreferences,
  PreferenceSections,
} from 'utils/preferences';
import dayjs from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import isoWeek from 'dayjs/plugin/isoWeek';
import { VatReturnLog } from 'store/vat/models/vatReturnLog';
import { MappedReturn } from 'store/returns/models/returnData';
import { HomeComponent } from 'components/HomeComponent';
import { PwaHomeEntityList } from 'components/PwaHomeEntityList';
import {
  CARD_REGISTRY,
  getDefaultCardOrderForView,
} from 'components/HomeComponent/cards/cardRegistry';
import { CardId } from 'components/HomeComponent/cards/types';
import { migrateDashboardCardIds } from 'utils/migrate-dashboard-card-ids';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';
import { PINNED_ENTITY_IDS_PREF } from 'utils/use-pwa-capture-entities';
import {
  buildFilterConfig,
  filterConfigToSearchParams,
  FilterConfig,
} from 'utils/filterHelpers';
import {
  getMyOpenReturnsFilterConfig,
  getNotStartedFilterConfig,
  getTeamReturnsFilterConfig,
  getAwaitingReviewFilterConfig,
  getSubmittedThisMonthFilterConfig,
  getReturnsByDueDateFilterConfig,
  getTypeOfReturnsFilterConfig,
  getYourRoleInReturnsFilterConfig,
  getTeamAllocationPersonFilterConfig,
  cardFilterConfigToSearchParams,
  CardFilterConfig,
} from 'utils/cardFilterConfig';

dayjs.extend(quarterOfYear);
dayjs.extend(isoWeek);

export const PAGE_SIZE_MAIN = 'page-size-main';
export const PAGE_SIZE_CAPTURED_SUBMISSIONS = 'page-size-captured-submissions';
export const MAIN_TUTORIAL_HIDE = 'main-tutorial-hide';
export const SHOW_ONLY_MY_RETURNS = 'show-only-my-returns';
export const SUBMITTED_RETURNS_DATE_FILTER = 'submitted-returns-date-filter';
export const TIME_TO_SUBMIT_DATE_FILTER = 'time-to-submit-date-filter';
export const DASHBOARD_VIEW_TYPE = 'dashboard-view-type';
export const DASHBOARD_CARD_ORDER = 'dashboard-card-order';
export const DASHBOARD_CARD_HEIGHTS = 'dashboard-card-heights';
export const DASHBOARD_CARD_WIDTHS = 'dashboard-card-widths';
export const DASHBOARD_CARD_LAYOUTS = 'dashboard-card-layouts';
export const DASHBOARD_CARD_VISIBILITY = 'dashboard-card-visibility';

export const HomePage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const mappedReturns = useSelector(getOrgMappedReturns);
  const entities = useSelector(getOrgOrganisations);
  const currentUser = useSelector(getAuthUser);
  const isLoading = useSelector(getOrgLoading);
  const receiptStatistics = useSelector(getReceiptStatisticsAll);
  useReceiptStatisticsAll();
  const preferences = usePreferences();
  const pwaMobileNav = usePwaMobileNavEnabled();

  const handlePreferences = (
    action: PreferenceActions,
    preference: Preference
  ) => {
    if (currentUser)
      return preferences(
        action,
        PreferenceSections.MAIN,
        preference,
        currentUser?.userUuid
      );
    return false;
  };

  const pinnedEntityIds = React.useMemo(() => {
    if (!currentUser?.userUuid) return [];
    const stored = preferences(
      PreferenceActions.GET,
      PreferenceSections.ENTITIES,
      { name: PINNED_ENTITY_IDS_PREF },
      currentUser.userUuid
    );
    return Array.isArray(stored)
      ? stored.filter((id): id is string => typeof id === 'string')
      : [];
  }, [currentUser?.userUuid, currentUser?.prefDetails, preferences]);

  // Get saved preference for toggle, default to true
  const savedTogglePreference = handlePreferences(PreferenceActions.GET, {
    name: SHOW_ONLY_MY_RETURNS,
  });
  const [showOnlyMyReturns, setShowOnlyMyReturns] = useState(
    savedTogglePreference !== false
  );
  const isInitialMount = useRef(true);

  // Get saved preference for date filter
  const savedDateFilterPreference = currentUser
    ? handlePreferences(PreferenceActions.GET, {
        name: SUBMITTED_RETURNS_DATE_FILTER,
      })
    : null;
  const filterKey = 'submissionDate';
  const [submittedDateRange, setSubmittedDateRange] = useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null]
  >([null, null]);
  const [submittedDateRangeOption, setSubmittedDateRangeOption] = useState<
    string | any[]
  >(
    savedDateFilterPreference && typeof savedDateFilterPreference === 'string'
      ? savedDateFilterPreference
      : []
  );
  const [enableCustomRange, setEnableCustomRange] = useState(false);
  const [submittedReturnsData, setSubmittedReturnsData] = useState<any[]>([]);
  const [submittedDateFilterOpen, setSubmittedDateFilterOpen] = useState(false);
  const [filteredSubmittedReturns, setFilteredSubmittedReturns] = useState<
    MappedReturn[]
  >([]);

  // State for time to submit date filter
  const savedTimeToSubmitDateFilterPreference = currentUser
    ? handlePreferences(PreferenceActions.GET, {
        name: TIME_TO_SUBMIT_DATE_FILTER,
      })
    : null;
  const [timeToSubmitDateRange, setTimeToSubmitDateRange] = useState<
    [dayjs.Dayjs | null, dayjs.Dayjs | null]
  >([null, null]);
  const [timeToSubmitDateRangeOption, setTimeToSubmitDateRangeOption] =
    useState<string | any[]>(
      savedTimeToSubmitDateFilterPreference &&
        typeof savedTimeToSubmitDateFilterPreference === 'string'
        ? savedTimeToSubmitDateFilterPreference
        : DateOptions.THIS_YEAR
    );
  const [enableTimeToSubmitCustomRange, setEnableTimeToSubmitCustomRange] =
    useState(false);
  const [timeToSubmitDateFilterOpen, setTimeToSubmitDateFilterOpen] =
    useState(false);
  const [filteredTimeToSubmitLogs, setFilteredTimeToSubmitLogs] = useState<
    VatReturnLog[]
  >([]);

  // Manager View only when the user flag is enabled (not just account role).
  const isManager = React.useMemo(() => {
    if (!currentUser) return false;
    const AccountType = require('store/api/enums').AccountType;
    // Clients never get Manager View.
    if (AccountType[currentUser.accountType] === AccountType.CLIENT) {
      return false;
    }
    return currentUser.managerViewEnabled === true;
  }, [currentUser]);

  // Dashboard view type: 'manager' or 'my'
  const savedViewTypePreference = currentUser
    ? handlePreferences(PreferenceActions.GET, {
        name: DASHBOARD_VIEW_TYPE,
      })
    : null;
  const [dashboardViewType, setDashboardViewType] = useState<'manager' | 'my'>(
    () => {
      if (
        savedViewTypePreference === 'manager' &&
        currentUser?.managerViewEnabled === true
      ) {
        return 'manager';
      }
      return 'my';
    }
  );

  // If Manager View is disabled for this user, force My View.
  useEffect(() => {
    if (!isManager && dashboardViewType === 'manager') {
      setDashboardViewType('my');
    }
  }, [isManager, dashboardViewType]);

  const captureDashboardStats = React.useMemo(
    () =>
      getDashboardReceiptStatistics(
        receiptStatistics,
        dashboardViewType,
        mappedReturns,
        currentUser
      ),
    [receiptStatistics, dashboardViewType, mappedReturns, currentUser]
  );

  // Save view type preference when it changes
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }
    if (currentUser) {
      handlePreferences(PreferenceActions.SET, {
        name: DASHBOARD_VIEW_TYPE,
        value: dashboardViewType,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardViewType]);

  // Get saved card order preference - separate for each view type
  const getCardOrderPreferenceKey = (viewType: 'manager' | 'my') => {
    return `${DASHBOARD_CARD_ORDER}_${viewType}`;
  };

  // Get saved card visibility preference - separate for each view type
  const getCardVisibilityPreferenceKey = (viewType: 'manager' | 'my') => {
    return `${DASHBOARD_CARD_VISIBILITY}_${viewType}`;
  };

  // Use getDefaultCardOrderForView from cardRegistry.ts (single source of truth)

  const savedCardOrderPreference = currentUser
    ? handlePreferences(PreferenceActions.GET, {
        name: getCardOrderPreferenceKey(dashboardViewType),
      })
    : null;

  // Initialize with saved preference or system defaults (never save defaults to preferences)
  const getInitialCardOrder = () => {
    if (
      savedCardOrderPreference &&
      Array.isArray(savedCardOrderPreference) &&
      savedCardOrderPreference.length > 0
    ) {
      return migrateDashboardCardIds(savedCardOrderPreference);
    }
    // Return system defaults (not saved to preferences)
    return getDefaultCardOrderForView(dashboardViewType);
  };

  const [cardOrder, setCardOrder] = useState<string[]>(getInitialCardOrder());

  // Update card order when view type changes
  React.useEffect(() => {
    if (currentUser) {
      const savedOrder = handlePreferences(PreferenceActions.GET, {
        name: getCardOrderPreferenceKey(dashboardViewType),
      });
      if (savedOrder && Array.isArray(savedOrder) && savedOrder.length > 0) {
        setCardOrder(migrateDashboardCardIds(savedOrder));
      } else {
        // No saved preference - use system defaults (don't save them)
        const defaultOrder = getDefaultCardOrderForView(dashboardViewType);
        setCardOrder(defaultOrder);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardViewType, currentUser, getDefaultCardOrderForView]);

  // Save card order preference when it changes
  const saveCardOrder = React.useCallback(
    (newOrder: string[]) => {
      if (currentUser) {
        if (newOrder.length === 0) {
          // Empty array means reset to system defaults - clear preference by setting to null
          handlePreferences(PreferenceActions.SET, {
            name: getCardOrderPreferenceKey(dashboardViewType),
            value: null,
          });
          // Set to system defaults
          const defaultOrder = getDefaultCardOrderForView(dashboardViewType);
          setCardOrder(defaultOrder);
        } else {
          // Save user's custom order (keep KPIs above the large info trio)
          const canonicalOrder = migrateDashboardCardIds(newOrder);
          handlePreferences(PreferenceActions.SET, {
            name: getCardOrderPreferenceKey(dashboardViewType),
            value: canonicalOrder,
          });
          setCardOrder(canonicalOrder);
        }
      }
    },
    [
      currentUser,
      handlePreferences,
      dashboardViewType,
      getDefaultCardOrderForView,
    ]
  );

  // Card visibility management
  const savedCardVisibilityPreference = currentUser
    ? handlePreferences(PreferenceActions.GET, {
        name: getCardVisibilityPreferenceKey(dashboardViewType),
      })
    : null;

  // Initialize visible cards - if no preference, all default cards are visible
  const getInitialVisibleCards = React.useCallback(() => {
    if (
      savedCardVisibilityPreference &&
      Array.isArray(savedCardVisibilityPreference) &&
      savedCardVisibilityPreference.length > 0
    ) {
      return migrateDashboardCardIds(savedCardVisibilityPreference);
    }
    // Default: all cards in the default order are visible
    return getDefaultCardOrderForView(dashboardViewType);
  }, [
    savedCardVisibilityPreference,
    dashboardViewType,
    getDefaultCardOrderForView,
  ]);

  const [visibleCards, setVisibleCards] = useState<string[]>(
    getInitialVisibleCards()
  );

  // Ref to track if we just saved visibility (to prevent useEffect from overwriting)
  const justSavedVisibilityRef = React.useRef(false);

  // Update visible cards when view type changes
  React.useEffect(() => {
    // CRITICAL: Don't reset visibleCards if we just saved it
    // This prevents the useEffect from overwriting the state immediately after a save
    if (justSavedVisibilityRef.current) {
      justSavedVisibilityRef.current = false;
      return;
    }

    if (currentUser) {
      const savedVisibility = handlePreferences(PreferenceActions.GET, {
        name: getCardVisibilityPreferenceKey(dashboardViewType),
      });
      if (
        savedVisibility &&
        Array.isArray(savedVisibility) &&
        savedVisibility.length > 0
      ) {
        setVisibleCards(migrateDashboardCardIds(savedVisibility));
      } else {
        // No saved preference - use system defaults
        const defaultOrder = getDefaultCardOrderForView(dashboardViewType);
        setVisibleCards(defaultOrder);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardViewType, currentUser, getDefaultCardOrderForView]);

  // Save card visibility preference
  const saveCardVisibility = React.useCallback(
    (newVisibility: string[]) => {
      if (currentUser) {
        const defaultOrder = getDefaultCardOrderForView(dashboardViewType);
        const matchesDefault =
          JSON.stringify(newVisibility.sort()) ===
          JSON.stringify(defaultOrder.sort());

        // CRITICAL: Always save the visibility, even if it matches defaults
        // This ensures that when a card is removed and saved, the preference is not null
        // If we clear the preference when it matches defaults, the useEffect will reset visibleCards to defaults
        // which can cause removed cards to reappear
        // Only clear preference on explicit reset, not when saving
        handlePreferences(PreferenceActions.SET, {
          name: getCardVisibilityPreferenceKey(dashboardViewType),
          value: newVisibility,
        });

        // CRITICAL: Set flag to prevent useEffect from overwriting this update
        justSavedVisibilityRef.current = true;
        setVisibleCards(newVisibility);
        // Clear the flag after a short delay to allow useEffect to run normally next time
        setTimeout(() => {
          justSavedVisibilityRef.current = false;
        }, 100);
      }
    },
    [
      currentUser,
      handlePreferences,
      dashboardViewType,
      getDefaultCardOrderForView,
    ]
  );

  // Add a card to visibility (newest first)
  const addCard = React.useCallback(
    (cardId: string) => {
      if (!visibleCards.includes(cardId)) {
        const newVisibility = [cardId, ...visibleCards];
        saveCardVisibility(newVisibility);
        // Also add to card order if not present
        if (!cardOrder.includes(cardId)) {
          // Append then canonicalize so large info cards are never prepended above KPIs.
          saveCardOrder(migrateDashboardCardIds([...cardOrder, cardId]));
        }
      }
    },
    [visibleCards, cardOrder, saveCardVisibility, saveCardOrder]
  );

  // Remove a card from visibility
  const removeCard = React.useCallback(
    (cardId: string) => {
      const doRemove = () => {
        const newVisibility = visibleCards.filter((id) => id !== cardId);
        saveCardVisibility(newVisibility);
        const newOrder = cardOrder.filter((id) => id !== cardId);
        saveCardOrder(newOrder);
      };
      // Close Team Allocation dropdown first, then remove after delay so Select can tear down before unmount
      if (cardId === 'teamAllocation') {
        setTeamAllocationFilterOpen(false);
        setTimeout(doRemove, 150);
      } else {
        doRemove();
      }
    },
    [
      visibleCards,
      cardOrder,
      saveCardVisibility,
      saveCardOrder,
      setTeamAllocationFilterOpen,
    ]
  );

  // Get saved card heights preference - separate for each view type
  const getCardHeightsPreferenceKey = (viewType: 'manager' | 'my') => {
    return `${DASHBOARD_CARD_HEIGHTS}_${viewType}`;
  };

  const savedCardHeightsPreference = currentUser
    ? handlePreferences(PreferenceActions.GET, {
        name: getCardHeightsPreferenceKey(dashboardViewType),
      })
    : null;
  const [cardHeights, setCardHeights] = useState<Record<string, number> | null>(
    savedCardHeightsPreference &&
      typeof savedCardHeightsPreference === 'object' &&
      savedCardHeightsPreference !== null
      ? (savedCardHeightsPreference as Record<string, number>)
      : null
  );

  // Update card heights when view type changes
  React.useEffect(() => {
    if (currentUser) {
      const savedHeights = handlePreferences(PreferenceActions.GET, {
        name: getCardHeightsPreferenceKey(dashboardViewType),
      });
      if (
        savedHeights &&
        typeof savedHeights === 'object' &&
        savedHeights !== null
      ) {
        setCardHeights(savedHeights as Record<string, number>);
      } else {
        setCardHeights(null); // Use default heights
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardViewType, currentUser]);

  // Save card heights preference when it changes
  const saveCardHeights = React.useCallback(
    (newHeights: Record<string, number>) => {
      if (currentUser) {
        if (Object.keys(newHeights).length === 0) {
          // Empty object means reset to system defaults - clear preference by setting to null
          handlePreferences(PreferenceActions.SET, {
            name: getCardHeightsPreferenceKey(dashboardViewType),
            value: null,
          });
          setCardHeights(null); // Will use system defaults
        } else {
          // Save user's custom heights
          handlePreferences(PreferenceActions.SET, {
            name: getCardHeightsPreferenceKey(dashboardViewType),
            value: newHeights,
          });
          setCardHeights(newHeights);
        }
      }
    },
    [currentUser, handlePreferences, dashboardViewType]
  );

  // Get saved card widths preference - separate for each view type
  const getCardWidthsPreferenceKey = (viewType: 'manager' | 'my') => {
    return `${DASHBOARD_CARD_WIDTHS}_${viewType}`;
  };

  const savedCardWidthsPreference = currentUser
    ? handlePreferences(PreferenceActions.GET, {
        name: getCardWidthsPreferenceKey(dashboardViewType),
      })
    : null;
  const [cardWidths, setCardWidths] = useState<Record<string, number> | null>(
    savedCardWidthsPreference &&
      typeof savedCardWidthsPreference === 'object' &&
      savedCardWidthsPreference !== null
      ? (savedCardWidthsPreference as Record<string, number>)
      : null
  );

  // Update card widths when view type changes
  React.useEffect(() => {
    if (currentUser) {
      const savedWidths = handlePreferences(PreferenceActions.GET, {
        name: getCardWidthsPreferenceKey(dashboardViewType),
      });
      if (
        savedWidths &&
        typeof savedWidths === 'object' &&
        savedWidths !== null
      ) {
        setCardWidths(savedWidths as Record<string, number>);
      } else {
        setCardWidths(null); // Use default widths
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardViewType, currentUser]);

  // Save card widths preference when it changes
  const saveCardWidths = React.useCallback(
    (newWidths: Record<string, number>) => {
      if (currentUser) {
        if (Object.keys(newWidths).length === 0) {
          // Empty object means reset to system defaults - clear preference
          handlePreferences(PreferenceActions.SET, {
            name: getCardWidthsPreferenceKey(dashboardViewType),
            value: null,
          });
          setCardWidths(null); // Will use system defaults
        } else {
          // Save user's custom widths
          handlePreferences(PreferenceActions.SET, {
            name: getCardWidthsPreferenceKey(dashboardViewType),
            value: newWidths,
          });
          setCardWidths(newWidths);
        }
      }
    },
    [currentUser, handlePreferences, dashboardViewType]
  );

  // Get saved layouts preference key - separate for each view type
  const getCardLayoutsPreferenceKey = (viewType: 'manager' | 'my') => {
    return `${DASHBOARD_CARD_LAYOUTS}_${viewType}`;
  };

  const savedCardLayoutsPreference = currentUser
    ? handlePreferences(PreferenceActions.GET, {
        name: getCardLayoutsPreferenceKey(dashboardViewType),
      })
    : null;
  const [cardLayouts, setCardLayouts] = useState<{
    xl?: any[];
    lg?: any[];
    md?: any[];
    sm?: any[];
    xs?: any[];
    xxs?: any[];
  } | null>(
    savedCardLayoutsPreference &&
      typeof savedCardLayoutsPreference === 'object' &&
      savedCardLayoutsPreference !== null
      ? (savedCardLayoutsPreference as {
          xl?: any[];
          lg?: any[];
          md?: any[];
          sm?: any[];
          xs?: any[];
          xxs?: any[];
        })
      : null
  );

  // Update card layouts when view type changes
  React.useEffect(() => {
    if (currentUser) {
      const savedLayouts = handlePreferences(PreferenceActions.GET, {
        name: getCardLayoutsPreferenceKey(dashboardViewType),
      });
      if (
        savedLayouts &&
        typeof savedLayouts === 'object' &&
        savedLayouts !== null
      ) {
        // CRITICAL: Validate and clean up invalid dimensions when loading saved layouts
        // This ensures that invalid dimensions (like 1x1) don't affect the current view
        // Remove any layouts with invalid dimensions (w < 2 or h < 2)
        const cleanedLayouts: any = {};
        let hasValidLayouts = false;

        // Filter out any layouts with invalid dimensions (w < 2 or h < 2)

        Object.keys(savedLayouts).forEach((breakpoint) => {
          const bp = breakpoint as keyof typeof savedLayouts;
          if (savedLayouts[bp] && Array.isArray(savedLayouts[bp])) {
            // Filter out any layouts with invalid dimensions (w < 2 or h < 2)
            // This removes 1x1 cards that might have been saved incorrectly
            const validLayouts = (savedLayouts[bp] as any[]).filter(
              (item: any) => {
                return item.w >= 2 && item.h >= 2;
              }
            );
            if (validLayouts.length > 0) {
              cleanedLayouts[bp] = validLayouts;
              hasValidLayouts = true;
            }
          }
        });

        // Only use cleaned layouts if they have valid data, otherwise use defaults
        if (hasValidLayouts) {
          setCardLayouts(
            cleanedLayouts as {
              xl?: any[];
              lg?: any[];
              md?: any[];
              sm?: any[];
              xs?: any[];
              xxs?: any[];
            }
          );
        } else {
          setCardLayouts(null); // Use default layouts if all saved layouts were invalid
        }
      } else {
        setCardLayouts(null); // Use default layouts
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardViewType]);

  const saveCardLayouts = React.useCallback(
    (newLayouts: {
      xl?: any[];
      lg?: any[];
      md?: any[];
      sm?: any[];
      xs?: any[];
      xxs?: any[];
    }) => {
      if (currentUser) {
        if (!newLayouts || Object.keys(newLayouts).length === 0) {
          // Empty object means reset to system defaults - clear preference
          handlePreferences(PreferenceActions.SET, {
            name: getCardLayoutsPreferenceKey(dashboardViewType),
            value: null,
          });
          setCardLayouts(null); // Will use system defaults
        } else {
          // CRITICAL: Ensure ALL breakpoints are present (xl, lg, md, sm, xs, xxs)
          // If a breakpoint is missing, use lg as fallback (most common)
          const allBreakpoints: Array<
            'xl' | 'lg' | 'md' | 'sm' | 'xs' | 'xxs'
          > = ['xl', 'lg', 'md', 'sm', 'xs', 'xxs'];
          const completeLayouts: {
            xl?: any[];
            lg?: any[];
            md?: any[];
            sm?: any[];
            xs?: any[];
            xxs?: any[];
          } = { ...newLayouts };
          allBreakpoints.forEach((bp) => {
            if (!completeLayouts[bp] && completeLayouts.lg) {
              // Use lg as fallback for missing breakpoints
              completeLayouts[bp] = completeLayouts.lg;
            }
          });
          // Save user's custom layouts (with all breakpoints)
          handlePreferences(PreferenceActions.SET, {
            name: getCardLayoutsPreferenceKey(dashboardViewType),
            value: completeLayouts,
          });
          setCardLayouts(completeLayouts);
        }
      }
    },
    [currentUser, handlePreferences, dashboardViewType]
  );

  // Save card visibility in batch (for CardSelector Save button)
  // Defined here after cardLayouts, cardHeights, and cardWidths are available
  // CRITICAL: Accept currentLayouts parameter to preserve positions when cardLayouts is null
  const saveCardVisibilityBatch = React.useCallback(
    (
      newVisibility: string[],
      currentLayouts?: {
        xl?: any[];
        lg?: any[];
        md?: any[];
        sm?: any[];
        xs?: any[];
        xxs?: any[];
      }
    ) => {
      // Save visibility
      saveCardVisibility(newVisibility);

      // Get default order for current view type
      const defaultOrder = getDefaultCardOrderForView(dashboardViewType);

      // Find cards that were added (in newVisibility but not in cardOrder)
      const addedCards = newVisibility.filter((id) => !cardOrder.includes(id));

      // Find cards that were removed (in cardOrder but not in newVisibility)
      const removedCards = cardOrder.filter(
        (id) => !newVisibility.includes(id)
      );

      // Clear saved layouts, heights, and widths for removed cards
      // Also clear saved layouts for added cards to ensure they use default positions/sizes
      const cardsToClear = [...removedCards, ...addedCards];

      if (cardsToClear.length > 0) {
        // CRITICAL: When cards are removed, filter them out from saved layouts
        // BUT preserve the exact positions (x, y) of remaining cards
        // This ensures that when a card is removed, no other cards move automatically
        // The merge logic in HomeComponent will use the preserved positions for remaining cards
        // IMPORTANT: Only filter cards that are being removed/added in the CURRENT view
        // This ensures that changes in one view (e.g., My View) do not affect the other view (e.g., Manager View)
        // Each view maintains its own separate layout matrix
        // CRITICAL: Always try to preserve current layouts, even if cardLayouts is null
        // Priority: currentLayouts (from pendingLayouts) > cardLayouts > preferences
        // This ensures we preserve positions when cards are removed in edit mode
        let layoutsToUpdate: typeof cardLayouts = cardLayouts;

        // CRITICAL: Check if currentLayouts has actual layout arrays (not just empty object)
        const hasValidCurrentLayouts =
          currentLayouts &&
          ((currentLayouts.xl && currentLayouts.xl.length > 0) ||
            (currentLayouts.lg && currentLayouts.lg.length > 0) ||
            (currentLayouts.md && currentLayouts.md.length > 0) ||
            (currentLayouts.sm && currentLayouts.sm.length > 0) ||
            (currentLayouts.xs && currentLayouts.xs.length > 0) ||
            (currentLayouts.xxs && currentLayouts.xxs.length > 0));

        // First, try to use currentLayouts passed from HomeComponent (from pendingLayouts or currentLayoutRef)
        // This is the most reliable source when in edit mode and cardLayouts is null
        if (hasValidCurrentLayouts) {
          layoutsToUpdate = currentLayouts;
        }
        // If no currentLayouts, use cardLayouts if available
        else if (cardLayouts && Object.keys(cardLayouts).length > 0) {
          layoutsToUpdate = cardLayouts;
        }
        // If still no layouts, try to get from preferences
        else {
          const savedLayouts = handlePreferences(PreferenceActions.GET, {
            name: getCardLayoutsPreferenceKey(dashboardViewType),
          });
          if (
            savedLayouts &&
            typeof savedLayouts === 'object' &&
            savedLayouts !== null
          ) {
            layoutsToUpdate = savedLayouts as typeof cardLayouts;
          }
        }

        if (layoutsToUpdate) {
          const updatedLayouts: typeof layoutsToUpdate = { ...layoutsToUpdate };
          let hasChanges = false;

          // CRITICAL: Ensure ALL breakpoints are present (xl, lg, md, sm, xs, xxs)
          // If a breakpoint is missing, use lg as fallback (most common)
          const allBreakpoints: Array<
            'xl' | 'lg' | 'md' | 'sm' | 'xs' | 'xxs'
          > = ['xl', 'lg', 'md', 'sm', 'xs', 'xxs'];
          allBreakpoints.forEach((bp) => {
            if (!updatedLayouts[bp] && updatedLayouts.lg) {
              // Use lg as fallback for missing breakpoints
              updatedLayouts[bp] = updatedLayouts.lg;
            }
          });

          Object.keys(updatedLayouts).forEach((breakpoint) => {
            const bp = breakpoint as keyof typeof updatedLayouts;
            if (updatedLayouts[bp]) {
              // Filter out removed cards, but keep all remaining cards with their exact positions AND dimensions
              // Only filter cards that are in the current view's cardLayouts
              // This ensures we don't accidentally affect other views' layouts
              // CRITICAL: Ensure all remaining cards have valid dimensions (w, h) from CARD_REGISTRY if missing
              const filtered = updatedLayouts[bp]!.filter(
                (item: any) => !removedCards.includes(item.i)
              ).map((item: any) => {
                // CRITICAL: Ensure dimensions are preserved - if missing, use CARD_REGISTRY defaults
                const cardConfig = CARD_REGISTRY[item.i as CardId];
                const minW = cardConfig?.minWidth || 2;
                const minH = cardConfig?.minHeight || 2;
                const defaultW = cardConfig?.defaultWidth || 4;
                const defaultH = cardConfig?.defaultHeight || 4;

                // If dimensions are missing or invalid, use CARD_REGISTRY defaults
                const w =
                  item.w && typeof item.w === 'number' && item.w >= minW
                    ? item.w
                    : defaultW;
                const h =
                  item.h && typeof item.h === 'number' && item.h >= minH
                    ? item.h
                    : defaultH;

                return {
                  ...item,
                  w: Math.max(w, minW),
                  h: Math.max(h, minH),
                };
              });

              // CRITICAL: Add default layouts for newly added cards
              // This ensures added cards have correct positions and sizes from CARD_REGISTRY
              // Note: The merge logic in HomeComponent will handle positioning, but we need valid dimensions
              if (addedCards.length > 0) {
                addedCards.forEach((addedCardId) => {
                  // Check if card already exists in filtered (shouldn't, but just in case)
                  const existing = filtered.find(
                    (item: any) => item.i === addedCardId
                  );
                  if (!existing) {
                    // Generate a simple default layout entry with correct dimensions from CARD_REGISTRY
                    const cardConfig = CARD_REGISTRY[addedCardId as CardId];
                    const minW = cardConfig?.minWidth || 2;
                    const minH = cardConfig?.minHeight || 2;
                    const defaultW = cardConfig?.defaultWidth || 4;
                    const defaultH = cardConfig?.defaultHeight || 4;

                    // Place at bottom temporarily - the merge logic in HomeComponent will position it correctly
                    const maxY =
                      filtered.length > 0
                        ? Math.max(
                            ...filtered.map(
                              (item: any) => (item.y || 0) + (item.h || 0)
                            )
                          )
                        : 0;

                    filtered.push({
                      i: addedCardId,
                      x: 0, // Will be positioned by merge logic
                      y: maxY, // Place at bottom temporarily
                      w: Math.max(defaultW, minW),
                      h: Math.max(defaultH, minH),
                      minW,
                      minH,
                    });
                  }
                });
              }
              if (filtered.length !== updatedLayouts[bp]!.length) {
                updatedLayouts[bp] = filtered;
                hasChanges = true;
              } else {
                // Even if no cards were removed, ensure all cards have valid dimensions
                const needsUpdate = filtered.some(
                  (item: any, index: number) => {
                    const original = updatedLayouts[bp]![index];
                    return (
                      !original.w ||
                      !original.h ||
                      original.w <
                        (CARD_REGISTRY[item.i as CardId]?.minWidth || 2) ||
                      original.h <
                        (CARD_REGISTRY[item.i as CardId]?.minHeight || 2)
                    );
                  }
                );
                if (needsUpdate) {
                  updatedLayouts[bp] = filtered;
                  hasChanges = true;
                }
              }
            }
          });

          if (hasChanges) {
            // CRITICAL: Save updated layouts for CURRENT view only - remaining cards keep their exact positions
            // This prevents automatic movement when cards are removed
            // The saveCardLayouts function uses dashboardViewType to save to the correct preference key
            // This ensures My View and Manager View have separate layout matrices
            // IMPORTANT: Update cardLayouts state immediately so HomeComponent can use the filtered layouts
            // This prevents cards from moving when the layout recalculates
            setCardLayouts(updatedLayouts);
            saveCardLayouts(updatedLayouts);
          }
        }

        // Clear heights for removed/added cards
        if (cardHeights) {
          const updatedHeights = { ...cardHeights };
          cardsToClear.forEach((cardId) => {
            delete updatedHeights[cardId];
          });
          if (
            Object.keys(updatedHeights).length !==
            Object.keys(cardHeights).length
          ) {
            saveCardHeights(updatedHeights);
          }
        }

        // Clear widths for removed/added cards
        if (cardWidths) {
          const updatedWidths = { ...cardWidths };
          cardsToClear.forEach((cardId) => {
            delete updatedWidths[cardId];
          });
          if (
            Object.keys(updatedWidths).length !== Object.keys(cardWidths).length
          ) {
            saveCardWidths(updatedWidths);
          }
        }
      }

      // Build new order:
      // 1. Keep existing cards in their current order (if still visible)
      const existingOrder = cardOrder.filter((id) =>
        newVisibility.includes(id)
      );

      // 2. For each added card, insert it at its default position
      let newOrder = [...existingOrder];
      addedCards.forEach((addedCardId) => {
        // Find default position of this card
        const defaultIndex = defaultOrder.indexOf(addedCardId as CardId);
        if (defaultIndex >= 0) {
          // Find where to insert: after the last card that appears before it in default order
          let insertIndex = newOrder.length;
          for (let i = defaultIndex - 1; i >= 0; i--) {
            const cardBeforeInDefault = defaultOrder[i];
            const indexInNewOrder = newOrder.indexOf(cardBeforeInDefault);
            if (indexInNewOrder >= 0) {
              insertIndex = indexInNewOrder + 1;
              break;
            }
          }
          // If no card before it exists in new order, check if any card after it exists
          if (insertIndex === newOrder.length) {
            for (let i = defaultIndex + 1; i < defaultOrder.length; i++) {
              const cardAfterInDefault = defaultOrder[i];
              const indexInNewOrder = newOrder.indexOf(cardAfterInDefault);
              if (indexInNewOrder >= 0) {
                insertIndex = indexInNewOrder;
                break;
              }
            }
          }
          newOrder.splice(insertIndex, 0, addedCardId);
        } else {
          // If card not in default order, append it
          newOrder.push(addedCardId);
        }
      });

      // Remove cards that are no longer visible
      newOrder = newOrder.filter((id) => newVisibility.includes(id));

      saveCardOrder(newOrder);
    },
    [
      cardOrder,
      saveCardVisibility,
      saveCardOrder,
      dashboardViewType,
      getDefaultCardOrderForView,
      cardLayouts,
      saveCardLayouts,
      cardHeights,
      saveCardHeights,
      cardWidths,
      saveCardWidths,
    ]
  );

  // State for Team Allocation user filter (not saved to preferences)
  const accountUsers = useSelector(getUserMembers) || [];
  const TEAM_ALLOCATION_USER_FILTER = 'teamAllocationUserFilter';
  const [selectedUserNames, setSelectedUserNames] = useState<string[]>([]);
  const [tempSelectedUserNames, setTempSelectedUserNames] = useState<string[]>(
    []
  );
  const [teamAllocationFilterOpen, setTeamAllocationFilterOpen] =
    useState(false);
  const [hoveredTeamAllocation, setHoveredTeamAllocation] = useState(false);

  // Close all dashboard card filter dropdowns when switching My View <-> Manager View
  // Prevents flicker and stale open state when returning to Manager view
  useEffect(() => {
    setTeamAllocationFilterOpen(false);
    setTimeToSubmitDateFilterOpen(false);
    setSubmittedDateFilterOpen(false);
  }, [dashboardViewType]);

  // Save preference when toggle changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (currentUser) {
      handlePreferences(PreferenceActions.SET, {
        name: SHOW_ONLY_MY_RETURNS,
        value: showOnlyMyReturns,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOnlyMyReturns]);

  const calcProcessingReturns = () => {
    return mappedReturns?.filter(
      (item) =>
        item.dataLog &&
        item.status !== DataLogStatus.SUBMITTED &&
        item.status !== DataLogStatus.CANCELLED &&
        item.status !== DataLogStatus.LOCKED
    )?.length;
  };

  // Calculate pending tasks (returns with dataLog that are not submitted/cancelled/locked)
  const pendingTasks = calcProcessingReturns() || 0;

  // Calculate awaiting review (Awaiting Approval and Awaiting Client Approval)
  const awaitingReview =
    mappedReturns?.filter(
      (item) =>
        item.dataLog &&
        (item.status === DataLogStatus.AWAITING_APPROVAL ||
          item.status === DataLogStatus.AWAITING_CLIENT_APPROVAL)
    )?.length || 0;

  // Calculate not started returns (returns with status NOT_STARTED)
  const notStartedCount =
    mappedReturns?.filter((item) => item.status === DataLogStatus.NOT_STARTED)
      ?.length || 0;

  // Calculate submitted this month using the same data source as "Total Returns Submitted"
  const [submittedThisMonth, setSubmittedThisMonth] = useState(0);

  // Calculate submitted this month from the same data source
  useEffect(() => {
    try {
      const allSubmittedLogs: VatReturnLog[] = [];
      const seenLogs = new Set<string>();

      // Get all submitted logs from mappedReturns (single source of truth)
      if (mappedReturns && mappedReturns.length > 0) {
        mappedReturns.forEach((ret) => {
          if (
            ret &&
            ret.dataLog &&
            ret.dataLog.submissionDate &&
            ret.dataLog.status
          ) {
            try {
              if (
                DataLogStatus[ret.dataLog.status] === DataLogStatus.SUBMITTED
              ) {
                const logKey = `${ret.dataLog.uuid}-${ret.mappingId}-${ret.dataLog.submissionDate}`;
                if (!seenLogs.has(logKey)) {
                  seenLogs.add(logKey);
                  // CRITICAL: Attach mappingId from parent MappedReturn to dataLog
                  // This ensures the table can find the matching mappedReturn by mappingId
                  const logWithMapping = {
                    ...ret.dataLog,
                    mappingId: ret.mappingId,
                  };
                  allSubmittedLogs.push(logWithMapping);
                }
              }
            } catch (e) {
              // Silently handle errors
            }
          }
        });
      }

      const today = dayjs();
      const startDate = today.startOf('month');
      const endDate = today.startOf('month').add(1, 'month');

      const thisMonthLogs = allSubmittedLogs.filter((log: VatReturnLog) => {
        if (!log || !log.submissionDate) return false;
        try {
          const submissionDate = dayjs(log.submissionDate);
          if (!submissionDate.isValid()) {
            return false;
          }
          const subDate = submissionDate.startOf('day');
          const start = startDate.startOf('day');
          const end = endDate.startOf('day');

          const subTimestamp = subDate.valueOf();
          const startTimestamp = start.valueOf();
          const endTimestamp = end.valueOf();
          const isInRange =
            subTimestamp >= startTimestamp && subTimestamp < endTimestamp;

          return isInRange;
        } catch (e) {
          return false;
        }
      });

      setSubmittedThisMonth(thisMonthLogs.length);
    } catch (error) {
      setSubmittedThisMonth(0);
    }
  }, [mappedReturns]);

  // Helper function to check if return should be included based on view type
  const shouldIncludeReturn = (item: any) => {
    // In Manager View, show all returns
    if (dashboardViewType === 'manager') return true;
    // In My View, only show returns where user is assigned
    return (
      item.dataLog?.preparerUser?.userUuid === currentUser?.userUuid ||
      item.dataLog?.reviewerUser?.userUuid === currentUser?.userUuid ||
      item.dataLog?.clientUser?.userUuid === currentUser?.userUuid
    );
  };

  // Navigation handlers for bar clicks
  // New robust filter system - accepts FilterConfig for type safety
  const navigateToSubmissionsWithConfig = (filterConfig: FilterConfig) => {
    const params = filterConfigToSearchParams(filterConfig);
    navigate(`${routes.submissions}?${params.toString()}`);
  };

  // Backward compatible wrapper for HomeComponent interface
  const navigateToSubmissions = (filterType: string, filterValue?: string) => {
    navigateToSubmissionsWithConfig(buildFilterConfig(filterType, filterValue));
  };

  const handleStatusClick = (status: DataLogStatus) => {
    // Use new simple filter configuration system
    const filterConfig: CardFilterConfig = {
      filters: {
        status: {
          value: [status],
          displayInUI: true,
        },
      },
    };
    const params = cardFilterConfigToSearchParams(filterConfig);
    navigate(`${routes.submissions}?${params.toString()}`);
  };

  const handleDueDateClick = (dueDateType: string) => {
    // Use new simple filter configuration system
    // Pass dashboardViewType to determine if user filter should be applied
    // 'my' view: show only current user's returns for this due date
    // 'manager' view: show all users' returns for this due date
    const filterConfig = getReturnsByDueDateFilterConfig(
      dueDateType,
      currentUser,
      dashboardViewType
    );
    if (filterConfig) {
      const params = cardFilterConfigToSearchParams(filterConfig);
      navigate(`${routes.submissions}?${params.toString()}`);
    } else {
      // Fallback if no current user
      navigate(routes.submissions);
    }
  };

  const handleReturnTypeClick = (returnTypeName: string) => {
    // Use new simple filter configuration system
    // Pass dashboardViewType to determine if user filter should be applied
    // 'my' view: show only current user's returns for this type
    // 'manager' view: show all users' returns for this type
    const filterConfig = getTypeOfReturnsFilterConfig(
      returnTypeName,
      currentUser,
      dashboardViewType
    );
    if (filterConfig) {
      const params = cardFilterConfigToSearchParams(filterConfig);
      navigate(`${routes.submissions}?${params.toString()}`);
    } else {
      // Fallback if no current user
      navigate(routes.submissions);
    }
  };

  const handleRoleClick = (role: 'preparer' | 'reviewer') => {
    // Use new simple filter configuration system
    const filterConfig = getYourRoleInReturnsFilterConfig(role, currentUser);
    if (filterConfig) {
      const params = cardFilterConfigToSearchParams(filterConfig);
      navigate(`${routes.submissions}?${params.toString()}`);
    } else {
      // Fallback if no current user
      navigate(routes.submissions);
    }
  };

  const handlePersonClick = (
    personName: string,
    role: 'preparer' | 'reviewer'
  ) => {
    // Team Allocation (manager view): use new filter system like Returns by Due Date
    const filterConfig = getTeamAllocationPersonFilterConfig(personName, role);
    const params = cardFilterConfigToSearchParams(filterConfig);
    navigate(`${routes.submissions}?${params.toString()}`);
  };

  const handleTotalEntitiesClick = () => {
    navigate(routes.organisations.all);
  };

  const handleTotalReturnsClick = () => {
    // Use new simple filter configuration system
    const filterConfig = getMyOpenReturnsFilterConfig(currentUser);
    if (filterConfig) {
      const params = cardFilterConfigToSearchParams(filterConfig);
      navigate(`${routes.submissions}?${params.toString()}`);
    } else {
      // Fallback if no current user
      navigate(routes.submissions);
    }
  };

  const handleNotStartedClick = () => {
    // Use new simple filter configuration system
    const filterConfig = getNotStartedFilterConfig();
    const params = cardFilterConfigToSearchParams(filterConfig);
    navigate(`${routes.submissions}?${params.toString()}`);
  };

  const handlePendingTasksClick = () => {
    // Use new simple filter configuration system
    const filterConfig = getTeamReturnsFilterConfig();
    const params = cardFilterConfigToSearchParams(filterConfig);
    navigate(`${routes.submissions}?${params.toString()}`);
  };

  const handleAwaitingReviewClick = () => {
    // Use new simple filter configuration system
    const filterConfig = getAwaitingReviewFilterConfig();
    const params = cardFilterConfigToSearchParams(filterConfig);
    navigate(`${routes.submissions}?${params.toString()}`);
  };

  const handleSubmittedThisMonthClick = () => {
    // Use new simple filter configuration system
    // Pass dashboardViewType to determine if user filter should be applied
    // 'my' view: show only current user's submissions
    // 'manager' view: show all users' submissions
    const filterConfig = getSubmittedThisMonthFilterConfig(
      currentUser,
      dashboardViewType
    );
    if (filterConfig) {
      const params = cardFilterConfigToSearchParams(filterConfig);
      navigate(`${routes.submissions}?${params.toString()}`);
    } else {
      // Fallback if no current user
      navigate(routes.submissions);
    }
  };

  const openSubmission = (
    connectionId: string,
    mappingId: string,
    dataLogId: string
  ) => {
    if (connectionId && mappingId && dataLogId) {
      const returnTypeId = mappedReturns?.find(
        (mappedReturn) => mappedReturn.mappingId === mappingId
      )?.returnTypeId;
      navigate(reportViewPath(connectionId, mappingId, dataLogId, returnTypeId));
    }
  };

  const createSubmission = (connectionId: string, mappingId: string) => {
    if (!isPwaTaxReturnCreationAllowed()) {
      return;
    }
    if (connectionId && mappingId) {
      const modalData: DrawerData = {
        type: DrawerType.ADD_TAX_SUBMISSION,
        title: t('submissions.create-tax-submission'),
        component: (
          <VatOverviewPage connectionId={connectionId} mappingId={mappingId} />
        ),
      };
      dispatch(turnDrawerOn(modalData));
    }
  };

  // Calculate tasks by status - filter based on toggle
  const savedTasks =
    mappedReturns?.filter(
      (item) => item.status === DataLogStatus.SAVED && shouldIncludeReturn(item)
    )?.length || 0;
  const awaitingClientTasks =
    mappedReturns?.filter(
      (item) =>
        item.status === DataLogStatus.AWAITING_CLIENT_APPROVAL &&
        shouldIncludeReturn(item)
    )?.length || 0;
  const reviewerApprovedTasks =
    mappedReturns?.filter(
      (item) =>
        item.status === DataLogStatus.INTERNAL_APPROVED &&
        shouldIncludeReturn(item)
    )?.length || 0;

  // Additional statuses for "All Returns" view
  const awaitingApprovalTasks =
    mappedReturns?.filter(
      (item) =>
        item.status === DataLogStatus.AWAITING_APPROVAL &&
        shouldIncludeReturn(item)
    )?.length || 0;
  const approvedTasks =
    mappedReturns?.filter(
      (item) =>
        item.status === DataLogStatus.APPROVED && shouldIncludeReturn(item)
    )?.length || 0;
  const clientApprovedTasks =
    mappedReturns?.filter(
      (item) =>
        item.status === DataLogStatus.CLIENT_APPROVED &&
        shouldIncludeReturn(item)
    )?.length || 0;
  const lockedTasks =
    mappedReturns?.filter(
      (item) =>
        item.status === DataLogStatus.LOCKED && shouldIncludeReturn(item)
    )?.length || 0;
  const submittedTasks =
    mappedReturns?.filter(
      (item) =>
        item.status === DataLogStatus.SUBMITTED && shouldIncludeReturn(item)
    )?.length || 0;
  const cancelledTasks =
    mappedReturns?.filter(
      (item) =>
        item.status === DataLogStatus.CANCELLED && shouldIncludeReturn(item)
    )?.length || 0;
  const notSavedTasks =
    mappedReturns?.filter(
      (item) =>
        item.status === DataLogStatus.NOT_SAVED && shouldIncludeReturn(item)
    )?.length || 0;
  const exceptionsModifiedTasks =
    mappedReturns?.filter(
      (item) =>
        item.status === DataLogStatus.EXCEPTIONS_MODIFIED &&
        shouldIncludeReturn(item)
    )?.length || 0;

  // Returns without status (empty or undefined) or with NOT_STARTED status - renamed to "Not Started"
  const notStartedTasks =
    mappedReturns?.filter(
      (item) =>
        shouldIncludeReturn(item) &&
        (!item.status ||
          item.status === '' ||
          item.status === DataLogStatus.NOT_STARTED)
    )?.length || 0;

  // Calculate returns by due date - filter based on toggle
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const weekFromNow = new Date(today);
  weekFromNow.setDate(weekFromNow.getDate() + 7);
  const monthFromNow = new Date(today);
  monthFromNow.setMonth(monthFromNow.getMonth() + 1);
  const nextMonthFromNow = new Date(today);
  nextMonthFromNow.setMonth(nextMonthFromNow.getMonth() + 2);

  const overdueReturns =
    mappedReturns?.filter((item) => {
      if (!item.nextDueDate || !shouldIncludeReturn(item)) return false;
      // Exclude the same statuses that the filter excludes: SUBMITTED, CANCELLED, NOT_STARTED
      if (
        item.status === DataLogStatus.SUBMITTED ||
        item.status === DataLogStatus.CANCELLED ||
        item.status === DataLogStatus.NOT_STARTED ||
        !item.status ||
        item.status === ''
      ) {
        return false;
      }
      // Only include the same open statuses that the filter includes
      const allowedStatuses = [
        DataLogStatus.LOCKED,
        DataLogStatus.SAVED,
        DataLogStatus.AWAITING_APPROVAL,
        DataLogStatus.INTERNAL_APPROVED,
        DataLogStatus.APPROVED,
        DataLogStatus.AWAITING_CLIENT_APPROVAL,
        DataLogStatus.CLIENT_APPROVED,
      ];
      if (!allowedStatuses.includes(item.status)) return false;
      const dueDate = new Date(item.nextDueDate);
      return dueDate < today;
    })?.length || 0;

  const dueTodayReturns =
    mappedReturns?.filter((item) => {
      if (!item.nextDueDate || !shouldIncludeReturn(item)) return false;
      // Exclude the same statuses that the filter excludes: SUBMITTED, CANCELLED, NOT_STARTED
      if (
        item.status === DataLogStatus.SUBMITTED ||
        item.status === DataLogStatus.CANCELLED ||
        item.status === DataLogStatus.NOT_STARTED ||
        !item.status ||
        item.status === ''
      ) {
        return false;
      }
      // Only include the same open statuses that the filter includes
      const allowedStatuses = [
        DataLogStatus.LOCKED,
        DataLogStatus.SAVED,
        DataLogStatus.AWAITING_APPROVAL,
        DataLogStatus.INTERNAL_APPROVED,
        DataLogStatus.APPROVED,
        DataLogStatus.AWAITING_CLIENT_APPROVAL,
        DataLogStatus.CLIENT_APPROVED,
      ];
      if (!allowedStatuses.includes(item.status)) return false;
      const dueDate = new Date(item.nextDueDate);
      return (
        dueDate.getDate() === today.getDate() &&
        dueDate.getMonth() === today.getMonth() &&
        dueDate.getFullYear() === today.getFullYear()
      );
    })?.length || 0;

  const dueThisWeekReturns =
    mappedReturns?.filter((item) => {
      if (!item.nextDueDate || !shouldIncludeReturn(item)) return false;
      // Exclude the same statuses that the filter excludes: SUBMITTED, CANCELLED, NOT_STARTED
      if (
        item.status === DataLogStatus.SUBMITTED ||
        item.status === DataLogStatus.CANCELLED ||
        item.status === DataLogStatus.NOT_STARTED ||
        !item.status ||
        item.status === ''
      ) {
        return false;
      }
      // Only include the same open statuses that the filter includes
      const allowedStatuses = [
        DataLogStatus.LOCKED,
        DataLogStatus.SAVED,
        DataLogStatus.AWAITING_APPROVAL,
        DataLogStatus.INTERNAL_APPROVED,
        DataLogStatus.APPROVED,
        DataLogStatus.AWAITING_CLIENT_APPROVAL,
        DataLogStatus.CLIENT_APPROVED,
      ];
      if (!allowedStatuses.includes(item.status)) return false;
      const dueDate = new Date(item.nextDueDate);
      return dueDate > today && dueDate <= weekFromNow;
    })?.length || 0;

  const dueThisMonthReturns =
    mappedReturns?.filter((item) => {
      if (!item.nextDueDate || !shouldIncludeReturn(item)) return false;
      // Exclude the same statuses that the filter excludes: SUBMITTED, CANCELLED, NOT_STARTED
      if (
        item.status === DataLogStatus.SUBMITTED ||
        item.status === DataLogStatus.CANCELLED ||
        item.status === DataLogStatus.NOT_STARTED ||
        !item.status ||
        item.status === ''
      ) {
        return false;
      }
      // Only include the same open statuses that the filter includes
      const allowedStatuses = [
        DataLogStatus.LOCKED,
        DataLogStatus.SAVED,
        DataLogStatus.AWAITING_APPROVAL,
        DataLogStatus.INTERNAL_APPROVED,
        DataLogStatus.APPROVED,
        DataLogStatus.AWAITING_CLIENT_APPROVAL,
        DataLogStatus.CLIENT_APPROVED,
      ];
      if (!allowedStatuses.includes(item.status)) return false;
      const dueDate = new Date(item.nextDueDate);
      return dueDate > weekFromNow && dueDate <= monthFromNow;
    })?.length || 0;

  const dueNextMonthReturns =
    mappedReturns?.filter((item) => {
      if (!item.nextDueDate || !shouldIncludeReturn(item)) return false;
      // Exclude the same statuses that the filter excludes: SUBMITTED, CANCELLED, NOT_STARTED
      if (
        item.status === DataLogStatus.SUBMITTED ||
        item.status === DataLogStatus.CANCELLED ||
        item.status === DataLogStatus.NOT_STARTED ||
        !item.status ||
        item.status === ''
      ) {
        return false;
      }
      // Only include the same open statuses that the filter includes
      const allowedStatuses = [
        DataLogStatus.LOCKED,
        DataLogStatus.SAVED,
        DataLogStatus.AWAITING_APPROVAL,
        DataLogStatus.INTERNAL_APPROVED,
        DataLogStatus.APPROVED,
        DataLogStatus.AWAITING_CLIENT_APPROVAL,
        DataLogStatus.CLIENT_APPROVED,
      ];
      if (!allowedStatuses.includes(item.status)) return false;
      const dueDate = new Date(item.nextDueDate);
      return dueDate > monthFromNow && dueDate <= nextMonthFromNow;
    })?.length || 0;

  const dueLaterReturns =
    mappedReturns?.filter((item) => {
      if (!item.nextDueDate || !shouldIncludeReturn(item)) return false;
      // Exclude the same statuses that the filter excludes: SUBMITTED, CANCELLED, NOT_STARTED
      if (
        item.status === DataLogStatus.SUBMITTED ||
        item.status === DataLogStatus.CANCELLED ||
        item.status === DataLogStatus.NOT_STARTED ||
        !item.status ||
        item.status === ''
      ) {
        return false;
      }
      // Only include the same open statuses that the filter includes
      const allowedStatuses = [
        DataLogStatus.LOCKED,
        DataLogStatus.SAVED,
        DataLogStatus.AWAITING_APPROVAL,
        DataLogStatus.INTERNAL_APPROVED,
        DataLogStatus.APPROVED,
        DataLogStatus.AWAITING_CLIENT_APPROVAL,
        DataLogStatus.CLIENT_APPROVED,
      ];
      if (!allowedStatuses.includes(item.status)) return false;
      const dueDate = new Date(item.nextDueDate);
      return dueDate > nextMonthFromNow;
    })?.length || 0;

  // Calculate role in returns (excluding submitted)
  const preparerCount =
    mappedReturns?.filter(
      (item) =>
        item.dataLog?.preparerUser?.userUuid === currentUser?.userUuid &&
        item.status !== DataLogStatus.SUBMITTED
    )?.length || 0;
  const reviewerCount =
    mappedReturns?.filter(
      (item) =>
        item.dataLog?.reviewerUser?.userUuid === currentUser?.userUuid &&
        item.status !== DataLogStatus.SUBMITTED
    )?.length || 0;
  const maxRoleValue = Math.max(preparerCount, reviewerCount, 1);

  // Calculate type of returns - key by "CountryName - Return Name" to match submissions table (mappingName uses returnType.countryName)
  const returnTypeCounts: { [key: string]: number } = {};
  mappedReturns?.forEach((item) => {
    if (
      item.returnTypeId &&
      shouldIncludeReturn(item) &&
      item.status !== DataLogStatus.SUBMITTED
    ) {
      let returnTypeName = 'Unknown';
      let countryName = 'Unknown';
      entities?.forEach((entity) => {
        const returnType = entity.returnTypesEdit?.find(
          (rt) => rt.id === item.returnTypeId
        );
        if (returnType) {
          returnTypeName = returnType.name || 'Unknown';
          countryName = returnType.countryName || 'Unknown';
        }
      });
      const key = `${countryName} - ${returnTypeName}`;
      returnTypeCounts[key] = (returnTypeCounts[key] || 0) + 1;
    }
  });
  const maxReturnTypeValue = Math.max(...Object.values(returnTypeCounts), 1);

  // Calculate people involved with returns - Preparer and Reviewer counts (excluding submitted)
  const preparerMap: { [key: string]: number } = {};
  const reviewerMap: { [key: string]: number } = {};

  mappedReturns?.forEach((item) => {
    // Exclude submitted returns
    if (item.status === DataLogStatus.SUBMITTED) return;
    if (item.dataLog?.preparerUser) {
      const preparerName =
        `${item.dataLog.preparerUser.firstName || ''} ${
          item.dataLog.preparerUser.lastName || ''
        }`.trim() ||
        item.dataLog.preparerUser.email ||
        'Unknown';
      preparerMap[preparerName] = (preparerMap[preparerName] || 0) + 1;
    }
    if (item.dataLog?.reviewerUser) {
      const reviewerName =
        `${item.dataLog.reviewerUser.firstName || ''} ${
          item.dataLog.reviewerUser.lastName || ''
        }`.trim() ||
        item.dataLog.reviewerUser.email ||
        'Unknown';
      reviewerMap[reviewerName] = (reviewerMap[reviewerName] || 0) + 1;
    }
  });

  const allUsers = new Set([
    ...Object.keys(preparerMap),
    ...Object.keys(reviewerMap),
  ]);
  const userData = Array.from(allUsers)
    .map((userName) => ({
      name: userName,
      preparerCount: preparerMap[userName] || 0,
      reviewerCount: reviewerMap[userName] || 0,
      totalCount: (preparerMap[userName] || 0) + (reviewerMap[userName] || 0),
    }))
    .sort((a, b) => b.totalCount - a.totalCount);

  // Normalize name for comparison (trim + collapse spaces) so filter works when formats differ slightly
  const normalizeName = (s: string) => (s || '').trim().replace(/\s+/g, ' ');
  const selectedNormalized = new Set(
    selectedUserNames.map((n) => normalizeName(n))
  );
  const filteredUserData =
    selectedUserNames.length > 0
      ? (() => {
          const matched = userData.filter((user) =>
            selectedNormalized.has(normalizeName(user.name))
          );
          const matchedNames = new Set(
            matched.map((u) => normalizeName(u.name))
          );
          const missingSelected = selectedUserNames.filter(
            (n) => !matchedNames.has(normalizeName(n))
          );
          const extraRows = missingSelected.map((name) => ({
            name,
            preparerCount: 0,
            reviewerCount: 0,
            totalCount: 0,
          }));
          return [...matched, ...extraRows].sort(
            (a, b) => b.totalCount - a.totalCount
          );
        })()
      : userData;

  // Process submitted returns and group by date
  useEffect(() => {
    let isMounted = true;

    try {
      let startDate: dayjs.Dayjs;
      let endDate: dayjs.Dayjs;

      // Check if custom range is enabled
      const isCustomRange =
        enableCustomRange ||
        submittedDateRangeOption === t('main.custom-range');

      // When "All" is selected or no option selected, show all data without date filtering
      if (
        (submittedDateRangeOption === DateOptions.ALL ||
          !submittedDateRangeOption ||
          (Array.isArray(submittedDateRangeOption) &&
            submittedDateRangeOption.length === 0)) &&
        !isCustomRange
      ) {
        // No date filtering - use all submitted returns
        startDate = null as any;
        endDate = null as any;
      } else if (
        isCustomRange &&
        submittedDateRange &&
        submittedDateRange.length === 2 &&
        submittedDateRange[0] &&
        submittedDateRange[1]
      ) {
        // Custom range overrides the date option selection
        startDate = dayjs(submittedDateRange[0]);
        endDate = dayjs(submittedDateRange[1]);
        if (!startDate.isValid() || !endDate.isValid()) {
          if (isMounted) {
            setSubmittedReturnsData([]);
            setFilteredSubmittedReturns([]);
          }
          return;
        }
      } else {
        const today = dayjs();
        switch (submittedDateRangeOption) {
          case DateOptions.TODAY:
            startDate = today.startOf('day');
            endDate = today.endOf('day').add(1, 'day');
            break;
          case DateOptions.THIS_WEEK:
            startDate = today.startOf('week');
            endDate = today.endOf('week').add(1, 'day');
            break;
          case DateOptions.LAST_WEEK:
            startDate = today.subtract(1, 'week').startOf('week');
            endDate = today.subtract(1, 'week').endOf('week').add(1, 'day');
            break;
          case DateOptions.THIS_MONTH:
            startDate = today.startOf('month');
            endDate = today.startOf('month').add(1, 'month');
            break;
          case DateOptions.LAST_MONTH:
            startDate = today.subtract(1, 'month').startOf('month');
            endDate = today.subtract(1, 'month').endOf('month').add(1, 'day');
            break;
          case DateOptions.THIS_QUARTER:
            startDate = today.startOf('quarter');
            endDate = today.endOf('quarter').add(1, 'day');
            break;
          case DateOptions.THIS_YEAR:
            startDate = today.startOf('year');
            endDate = today.endOf('year').add(1, 'day');
            break;
          case DateOptions.LAST_YEAR:
            startDate = today.subtract(1, 'year').startOf('year');
            endDate = today.subtract(1, 'year').endOf('year').add(1, 'day');
            break;
          case DateOptions.NEXT_WEEK:
            startDate = today.add(1, 'week').startOf('week');
            endDate = today.add(1, 'week').endOf('week').add(1, 'day');
            break;
          case DateOptions.NEXT_MONTH:
            startDate = today.add(1, 'month').startOf('month');
            endDate = today.add(1, 'month').endOf('month').add(1, 'day');
            break;
          case DateOptions.NEXT_QUARTER:
            startDate = today.add(1, 'quarter').startOf('quarter');
            endDate = today.add(1, 'quarter').endOf('quarter').add(1, 'day');
            break;
          default:
            startDate = today.startOf('year');
            endDate = today.endOf('year').add(1, 'day');
        }
      }

      // Filter mappedReturns directly by submission status
      const allSubmittedReturns: MappedReturn[] = [];
      const seenKeys = new Set<string>();

      if (mappedReturns && mappedReturns.length > 0) {
        mappedReturns.forEach((ret) => {
          if (
            ret &&
            ret.dataLog &&
            ret.dataLog.submissionDate &&
            ret.dataLog.status
          ) {
            try {
              if (
                DataLogStatus[ret.dataLog.status] === DataLogStatus.SUBMITTED
              ) {
                const logKey = `${ret.dataLog.uuid}-${ret.mappingId}-${ret.dataLog.submissionDate}`;
                if (!seenKeys.has(logKey)) {
                  seenKeys.add(logKey);
                  allSubmittedReturns.push(ret);
                }
              }
            } catch (e) {
              // Silently handle errors
            }
          }
        });
      }

      if (allSubmittedReturns.length === 0) {
        if (isMounted) {
          setSubmittedReturnsData([]);
          setFilteredSubmittedReturns([]);
        }
        return;
      }

      // Check if custom range is enabled
      const isCustomRangeForFilter =
        enableCustomRange ||
        submittedDateRangeOption === t('main.custom-range');

      // If "All" is selected or no option and custom range is not enabled, show all returns without date filtering
      let filteredReturns: MappedReturn[];
      if (
        (submittedDateRangeOption === DateOptions.ALL ||
          !submittedDateRangeOption ||
          (Array.isArray(submittedDateRangeOption) &&
            submittedDateRangeOption.length === 0)) &&
        !isCustomRangeForFilter
      ) {
        filteredReturns = allSubmittedReturns;
      } else {
        // Apply date filtering
        if (
          !startDate ||
          !endDate ||
          !startDate.isValid() ||
          !endDate.isValid()
        ) {
          if (isMounted) {
            setSubmittedReturnsData([]);
            setFilteredSubmittedReturns([]);
          }
          return;
        }

        filteredReturns = allSubmittedReturns.filter((ret: MappedReturn) => {
          if (!ret || !ret.dataLog || !ret.dataLog.submissionDate) return false;
          try {
            const submissionDate = dayjs(ret.dataLog.submissionDate);
            if (!submissionDate.isValid()) {
              return false;
            }
            const subDate = submissionDate.startOf('day');
            const start = startDate.startOf('day');
            const end = endDate.startOf('day');

            const subTimestamp = subDate.valueOf();
            const startTimestamp = start.valueOf();
            const endTimestamp = end.valueOf();

            const isInRange =
              subTimestamp >= startTimestamp && subTimestamp < endTimestamp;

            return isInRange;
          } catch (e) {
            return false;
          }
        });
      }

      if (isMounted) {
        setFilteredSubmittedReturns(filteredReturns);
      }

      // For grouping, calculate date range - if "All" is selected, find min/max dates from filtered returns
      let groupStartDate: dayjs.Dayjs;
      let groupEndDate: dayjs.Dayjs;

      const isCustomRangeForGroup =
        enableCustomRange ||
        submittedDateRangeOption === t('main.custom-range');

      if (
        (submittedDateRangeOption === DateOptions.ALL ||
          !submittedDateRangeOption ||
          (Array.isArray(submittedDateRangeOption) &&
            submittedDateRangeOption.length === 0)) &&
        !isCustomRangeForGroup
      ) {
        // Find min and max dates from all returns
        const dates = filteredReturns
          .filter((ret) => ret && ret.dataLog && ret.dataLog.submissionDate)
          .map((ret) => dayjs(ret.dataLog!.submissionDate))
          .filter((d) => d.isValid())
          .map((d) => d.valueOf());
        if (dates.length === 0) {
          if (isMounted) {
            setSubmittedReturnsData([]);
          }
          return;
        }
        const minTimestamp = Math.min(...dates);
        const maxTimestamp = Math.max(...dates);
        groupStartDate = dayjs(minTimestamp);
        groupEndDate = dayjs(maxTimestamp);
      } else {
        groupStartDate = startDate;
        groupEndDate = endDate;
      }

      const daysDiff = groupEndDate.diff(groupStartDate, 'day');
      let groupBy: 'day' | 'week' | 'month' = 'day';
      if (daysDiff > 90) {
        groupBy = 'month';
      } else if (daysDiff > 14) {
        groupBy = 'week';
      }

      const grouped = new Map<number, number>();
      filteredReturns.forEach((ret: MappedReturn) => {
        if (!ret || !ret.dataLog || !ret.dataLog.submissionDate) return;
        const submissionDate = dayjs(ret.dataLog.submissionDate);
        if (!submissionDate.isValid()) return;
        const sortKey = getChartGroupSortKey(submissionDate, groupBy);
        grouped.set(sortKey, (grouped.get(sortKey) ?? 0) + 1);
      });

      const data = Array.from(grouped.entries())
        .map(([sortKey, count]) => ({
          date: formatChartAxisLabel(dayjs(sortKey), groupBy, i18n.language, t),
          count,
          sortKey,
        }))
        .sort((a, b) => a.sortKey - b.sortKey);

      if (isMounted) {
        setSubmittedReturnsData(data);
      }
    } catch (error) {
      console.error('Error processing submitted returns:', error);
      if (isMounted) {
        setSubmittedReturnsData([]);
        setFilteredSubmittedReturns([]);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [
    mappedReturns,
    submittedDateRange,
    submittedDateRangeOption,
    enableCustomRange,
    t,
    i18n.language,
  ]);

  // Filter logs for time to submit card based on submission date
  useEffect(() => {
    let isMounted = true;

    try {
      let startDate: dayjs.Dayjs;
      let endDate: dayjs.Dayjs;

      const isCustomRange =
        enableTimeToSubmitCustomRange ||
        timeToSubmitDateRangeOption === t('main.custom-range');

      if (
        (timeToSubmitDateRangeOption === DateOptions.ALL ||
          !timeToSubmitDateRangeOption ||
          (Array.isArray(timeToSubmitDateRangeOption) &&
            timeToSubmitDateRangeOption.length === 0)) &&
        !isCustomRange
      ) {
        // No date filtering - use all submitted logs
        startDate = null as any;
        endDate = null as any;
      } else if (
        isCustomRange &&
        timeToSubmitDateRange &&
        timeToSubmitDateRange.length === 2 &&
        timeToSubmitDateRange[0] &&
        timeToSubmitDateRange[1]
      ) {
        startDate = dayjs(timeToSubmitDateRange[0]);
        endDate = dayjs(timeToSubmitDateRange[1]);
        if (!startDate.isValid() || !endDate.isValid()) {
          if (isMounted) {
            setFilteredTimeToSubmitLogs([]);
          }
          return;
        }
      } else {
        const today = dayjs();
        switch (timeToSubmitDateRangeOption) {
          case DateOptions.TODAY:
            startDate = today.startOf('day');
            endDate = today.endOf('day').add(1, 'day');
            break;
          case DateOptions.THIS_WEEK:
            startDate = today.startOf('week');
            endDate = today.endOf('week').add(1, 'day');
            break;
          case DateOptions.LAST_WEEK:
            startDate = today.subtract(1, 'week').startOf('week');
            endDate = today.subtract(1, 'week').endOf('week').add(1, 'day');
            break;
          case DateOptions.THIS_MONTH:
            startDate = today.startOf('month');
            endDate = today.startOf('month').add(1, 'month');
            break;
          case DateOptions.LAST_MONTH:
            startDate = today.subtract(1, 'month').startOf('month');
            endDate = today.subtract(1, 'month').endOf('month').add(1, 'day');
            break;
          case DateOptions.THIS_QUARTER:
            startDate = today.startOf('quarter');
            endDate = today.endOf('quarter').add(1, 'day');
            break;
          case DateOptions.THIS_YEAR:
            startDate = today.startOf('year');
            endDate = today.endOf('year').add(1, 'day');
            break;
          case DateOptions.LAST_YEAR:
            startDate = today.subtract(1, 'year').startOf('year');
            endDate = today.subtract(1, 'year').endOf('year').add(1, 'day');
            break;
          case DateOptions.NEXT_WEEK:
            startDate = today.add(1, 'week').startOf('week');
            endDate = today.add(1, 'week').endOf('week').add(1, 'day');
            break;
          case DateOptions.NEXT_MONTH:
            startDate = today.add(1, 'month').startOf('month');
            endDate = today.add(1, 'month').endOf('month').add(1, 'day');
            break;
          case DateOptions.NEXT_QUARTER:
            startDate = today.add(1, 'quarter').startOf('quarter');
            endDate = today.add(1, 'quarter').endOf('quarter').add(1, 'day');
            break;
          default:
            startDate = today.startOf('year');
            endDate = today.endOf('year').add(1, 'day');
        }
      }

      const allSubmittedLogs: VatReturnLog[] = [];
      const seenLogs = new Set<string>();

      // Get all submitted logs from mappedReturns (single source of truth)
      if (mappedReturns && mappedReturns.length > 0) {
        mappedReturns.forEach((ret) => {
          if (
            ret &&
            ret.dataLog &&
            ret.dataLog.submissionDate &&
            ret.dataLog.status &&
            ret.dataLog.generatedDate
          ) {
            try {
              if (
                DataLogStatus[ret.dataLog.status] === DataLogStatus.SUBMITTED
              ) {
                const logKey = `${ret.dataLog.uuid}-${ret.mappingId}-${ret.dataLog.submissionDate}`;
                if (!seenLogs.has(logKey)) {
                  seenLogs.add(logKey);
                  // CRITICAL: Attach mappingId from parent MappedReturn to dataLog
                  // This ensures the table can find the matching mappedReturn by mappingId
                  const logWithMapping = {
                    ...ret.dataLog,
                    mappingId: ret.mappingId,
                  };
                  allSubmittedLogs.push(logWithMapping);
                }
              }
            } catch (e) {
              // Silently handle errors
            }
          }
        });
      }

      let filteredLogs: VatReturnLog[];
      if (
        (timeToSubmitDateRangeOption === DateOptions.ALL ||
          !timeToSubmitDateRangeOption ||
          (Array.isArray(timeToSubmitDateRangeOption) &&
            timeToSubmitDateRangeOption.length === 0)) &&
        !isCustomRange
      ) {
        filteredLogs = allSubmittedLogs;
      } else {
        if (
          !startDate ||
          !endDate ||
          !startDate.isValid() ||
          !endDate.isValid()
        ) {
          if (isMounted) {
            setFilteredTimeToSubmitLogs([]);
          }
          return;
        }

        filteredLogs = allSubmittedLogs.filter((log: VatReturnLog) => {
          if (!log || !log.submissionDate) return false;
          try {
            const submissionDate = dayjs(log.submissionDate);
            if (!submissionDate.isValid()) {
              return false;
            }
            const subDate = submissionDate.startOf('day');
            const start = startDate.startOf('day');
            const end = endDate.startOf('day');

            const subTimestamp = subDate.valueOf();
            const startTimestamp = start.valueOf();
            const endTimestamp = end.valueOf();

            return (
              subTimestamp >= startTimestamp && subTimestamp < endTimestamp
            );
          } catch (e) {
            return false;
          }
        });
      }

      if (isMounted) {
        setFilteredTimeToSubmitLogs(filteredLogs);
      }
    } catch (error) {
      console.error('Error processing time to submit logs:', error);
      if (isMounted) {
        setFilteredTimeToSubmitLogs([]);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [
    mappedReturns,
    timeToSubmitDateRange,
    timeToSubmitDateRangeOption,
    enableTimeToSubmitCustomRange,
    t,
  ]);

  // Initialize time to submit date range based on default or saved preference on mount
  useEffect(() => {
    try {
      // If no saved preference and default is THIS_YEAR, initialize the date range
      if (
        !savedTimeToSubmitDateFilterPreference &&
        timeToSubmitDateRangeOption === DateOptions.THIS_YEAR
      ) {
        const today = dayjs();
        const start = today.startOf('year');
        const end = today.endOf('year').add(1, 'day');
        if (start && end && start.isValid() && end.isValid()) {
          setTimeToSubmitDateRange([start, end]);
        }
      } else if (
        savedTimeToSubmitDateFilterPreference &&
        timeToSubmitDateRangeOption === savedTimeToSubmitDateFilterPreference
      ) {
        // If "All" is selected, don't set date range
        if (savedTimeToSubmitDateFilterPreference === DateOptions.ALL) {
          return;
        }

        const today = dayjs();
        let start: dayjs.Dayjs;
        let end: dayjs.Dayjs;

        switch (savedTimeToSubmitDateFilterPreference) {
          case DateOptions.TODAY:
            start = today.startOf('day');
            end = today.endOf('day').add(1, 'day');
            break;
          case DateOptions.THIS_WEEK:
            start = today.startOf('week');
            end = today.endOf('week').add(1, 'day');
            break;
          case DateOptions.LAST_WEEK:
            start = today.subtract(1, 'week').startOf('week');
            end = today.subtract(1, 'week').endOf('week').add(1, 'day');
            break;
          case DateOptions.THIS_MONTH:
            start = today.startOf('month');
            end = today.startOf('month').add(1, 'month');
            break;
          case DateOptions.LAST_MONTH:
            start = today.subtract(1, 'month').startOf('month');
            end = today.subtract(1, 'month').endOf('month').add(1, 'day');
            break;
          case DateOptions.THIS_QUARTER:
            start = today.startOf('quarter');
            end = today.endOf('quarter').add(1, 'day');
            break;
          case DateOptions.THIS_YEAR:
            start = today.startOf('year');
            end = today.endOf('year').add(1, 'day');
            break;
          case DateOptions.LAST_YEAR:
            start = today.subtract(1, 'year').startOf('year');
            end = today.subtract(1, 'year').endOf('year').add(1, 'day');
            break;
          case DateOptions.NEXT_WEEK:
            start = today.add(1, 'week').startOf('week');
            end = today.add(1, 'week').endOf('week').add(1, 'day');
            break;
          case DateOptions.NEXT_MONTH:
            start = today.add(1, 'month').startOf('month');
            end = today.add(1, 'month').endOf('month').add(1, 'day');
            break;
          case DateOptions.NEXT_QUARTER:
            start = today.add(1, 'quarter').startOf('quarter');
            end = today.add(1, 'quarter').endOf('quarter').add(1, 'day');
            break;
          default:
            start = today.startOf('year');
            end = today.endOf('year').add(1, 'day');
        }
        if (start && end && start.isValid() && end.isValid()) {
          setTimeToSubmitDateRange([start, end]);
        }
      }
    } catch (error) {
      console.error(
        'Error initializing time to submit date range from preference:',
        error
      );
    }
  }, [savedTimeToSubmitDateFilterPreference, timeToSubmitDateRangeOption]);

  // Initialize date range based on saved preference on mount
  useEffect(() => {
    try {
      if (
        savedDateFilterPreference &&
        submittedDateRangeOption === savedDateFilterPreference
      ) {
        // If "All" is selected, don't set date range
        if (savedDateFilterPreference === DateOptions.ALL) {
          return;
        }

        const today = dayjs();
        let start: dayjs.Dayjs;
        let end: dayjs.Dayjs;

        switch (savedDateFilterPreference) {
          case DateOptions.TODAY:
            start = today.startOf('day');
            end = today.endOf('day').add(1, 'day');
            break;
          case DateOptions.THIS_WEEK:
            start = today.startOf('week');
            end = today.endOf('week').add(1, 'day');
            break;
          case DateOptions.LAST_WEEK:
            start = today.subtract(1, 'week').startOf('week');
            end = today.subtract(1, 'week').endOf('week').add(1, 'day');
            break;
          case DateOptions.THIS_MONTH:
            start = today.startOf('month');
            end = today.startOf('month').add(1, 'month');
            break;
          case DateOptions.LAST_MONTH:
            start = today.subtract(1, 'month').startOf('month');
            end = today.subtract(1, 'month').endOf('month').add(1, 'day');
            break;
          case DateOptions.THIS_QUARTER:
            start = today.startOf('quarter');
            end = today.endOf('quarter').add(1, 'day');
            break;
          case DateOptions.THIS_YEAR:
            start = today.startOf('year');
            end = today.endOf('year').add(1, 'day');
            break;
          case DateOptions.LAST_YEAR:
            start = today.subtract(1, 'year').startOf('year');
            end = today.subtract(1, 'year').endOf('year').add(1, 'day');
            break;
          case DateOptions.NEXT_WEEK:
            start = today.add(1, 'week').startOf('week');
            end = today.add(1, 'week').endOf('week').add(1, 'day');
            break;
          case DateOptions.NEXT_MONTH:
            start = today.add(1, 'month').startOf('month');
            end = today.add(1, 'month').endOf('month').add(1, 'day');
            break;
          case DateOptions.NEXT_QUARTER:
            start = today.add(1, 'quarter').startOf('quarter');
            end = today.add(1, 'quarter').endOf('quarter').add(1, 'day');
            break;
          default:
            start = today.startOf('year');
            end = today.endOf('year').add(1, 'day');
        }
        if (start && end && start.isValid() && end.isValid()) {
          setSubmittedDateRange([start, end]);
        }
      }
    } catch (error) {
      console.error('Error initializing date range from preference:', error);
    }
  }, [savedDateFilterPreference, submittedDateRangeOption]);

  // Handle date option select - updates immediately like ScopeTable
  const onSubmittedDateOptionSelect = (value: string) => {
    let date: any[];
    const today = dayjs();

    switch (value) {
      case DateOptions.TODAY:
        date = [today.startOf('day'), today.endOf('day').add(1, 'day')];
        break;
      case DateOptions.THIS_WEEK:
        date = [today.startOf('week'), today.endOf('week').add(1, 'day')];
        break;
      case DateOptions.THIS_MONTH:
        date = [today.startOf('month'), today.endOf('month').add(1, 'day')];
        break;
      case DateOptions.THIS_QUARTER:
        date = [today.startOf('quarter'), today.endOf('quarter').add(1, 'day')];
        break;
      case DateOptions.THIS_YEAR:
        date = [today.startOf('year'), today.endOf('year').add(1, 'day')];
        break;
      case DateOptions.LAST_WEEK:
        date = [
          today.subtract(1, 'week').startOf('week'),
          today.subtract(1, 'week').endOf('week').add(1, 'day'),
        ];
        break;
      case DateOptions.LAST_MONTH:
        date = [
          today.subtract(1, 'month').startOf('month'),
          today.subtract(1, 'month').endOf('month').add(1, 'day'),
        ];
        break;
      case DateOptions.LAST_YEAR:
        date = [
          today.subtract(1, 'year').startOf('year'),
          today.subtract(1, 'year').endOf('year').add(1, 'day'),
        ];
        break;
      case DateOptions.NEXT_WEEK:
        date = [
          today.add(1, 'week').startOf('week'),
          today.add(1, 'week').endOf('week').add(1, 'day'),
        ];
        break;
      case DateOptions.NEXT_MONTH:
        date = [
          today.add(1, 'month').startOf('month'),
          today.add(1, 'month').endOf('month').add(1, 'day'),
        ];
        break;
      case DateOptions.NEXT_QUARTER:
        date = [
          today.add(1, 'quarter').startOf('quarter'),
          today.add(1, 'quarter').endOf('quarter').add(1, 'day'),
        ];
        break;
      default:
        date = [null, null];
        break;
    }
    setSubmittedDateRange(date as [dayjs.Dayjs | null, dayjs.Dayjs | null]);
    setSubmittedDateRangeOption(value);

    if (currentUser) {
      handlePreferences(PreferenceActions.SET, {
        name: SUBMITTED_RETURNS_DATE_FILTER,
        value: value,
      });
    }
  };

  // Handle custom range checkbox change - matches ScopeTable behavior
  const onCustomRangeCheckboxChange = (e: any) => {
    if (e.target.checked) {
      setEnableCustomRange(true);
      setSubmittedDateRangeOption(t('main.custom-range'));
    } else {
      setEnableCustomRange(false);
      setSubmittedDateRangeOption([]);
    }
  };

  // Handle Apply button - just closes dropdown (filter applies automatically via useEffect)
  const handleApplyDateFilter = () => {
    setSubmittedDateFilterOpen(false);
  };

  // Handle Cancel button - resets and closes dropdown
  const handleCancelDateFilter = () => {
    setSubmittedDateRange([null, null]);
    setSubmittedDateRangeOption([]);
    setEnableCustomRange(false);
    setSubmittedDateFilterOpen(false);
  };

  // Handle dropdown open/close
  const handleOpenDateFilterDropdown = (open: boolean) => {
    if (
      !open &&
      (!submittedDateRangeOption ||
        (Array.isArray(submittedDateRangeOption) &&
          submittedDateRangeOption.length === 0))
    ) {
      setSubmittedDateRangeOption([]);
    }
    setSubmittedDateFilterOpen(open);
  };

  // Handle time to submit date option select
  const onTimeToSubmitDateOptionSelect = (value: string) => {
    let date: any[];
    const today = dayjs();

    switch (value) {
      case DateOptions.TODAY:
        date = [today.startOf('day'), today.endOf('day').add(1, 'day')];
        break;
      case DateOptions.THIS_WEEK:
        date = [today.startOf('week'), today.endOf('week').add(1, 'day')];
        break;
      case DateOptions.THIS_MONTH:
        date = [today.startOf('month'), today.endOf('month').add(1, 'day')];
        break;
      case DateOptions.THIS_QUARTER:
        date = [today.startOf('quarter'), today.endOf('quarter').add(1, 'day')];
        break;
      case DateOptions.THIS_YEAR:
        date = [today.startOf('year'), today.endOf('year').add(1, 'day')];
        break;
      case DateOptions.LAST_WEEK:
        date = [
          today.subtract(1, 'week').startOf('week'),
          today.subtract(1, 'week').endOf('week').add(1, 'day'),
        ];
        break;
      case DateOptions.LAST_MONTH:
        date = [
          today.subtract(1, 'month').startOf('month'),
          today.subtract(1, 'month').endOf('month').add(1, 'day'),
        ];
        break;
      case DateOptions.LAST_YEAR:
        date = [
          today.subtract(1, 'year').startOf('year'),
          today.subtract(1, 'year').endOf('year').add(1, 'day'),
        ];
        break;
      case DateOptions.NEXT_WEEK:
        date = [
          today.add(1, 'week').startOf('week'),
          today.add(1, 'week').endOf('week').add(1, 'day'),
        ];
        break;
      case DateOptions.NEXT_MONTH:
        date = [
          today.add(1, 'month').startOf('month'),
          today.add(1, 'month').endOf('month').add(1, 'day'),
        ];
        break;
      case DateOptions.NEXT_QUARTER:
        date = [
          today.add(1, 'quarter').startOf('quarter'),
          today.add(1, 'quarter').endOf('quarter').add(1, 'day'),
        ];
        break;
      default:
        date = [null, null];
        break;
    }
    setTimeToSubmitDateRange(date as [dayjs.Dayjs | null, dayjs.Dayjs | null]);
    setTimeToSubmitDateRangeOption(value);

    if (currentUser) {
      handlePreferences(PreferenceActions.SET, {
        name: TIME_TO_SUBMIT_DATE_FILTER,
        value: value,
      });
    }
  };

  // Handle time to submit custom range checkbox change
  const onTimeToSubmitCustomRangeCheckboxChange = (e: any) => {
    if (e.target.checked) {
      setEnableTimeToSubmitCustomRange(true);
      setTimeToSubmitDateRangeOption(t('main.custom-range'));
    } else {
      setEnableTimeToSubmitCustomRange(false);
      setTimeToSubmitDateRangeOption([]);
    }
  };

  // Handle time to submit Apply button
  const handleApplyTimeToSubmitDateFilter = () => {
    setTimeToSubmitDateFilterOpen(false);
  };

  // Handle time to submit Cancel button
  const handleCancelTimeToSubmitDateFilter = () => {
    setTimeToSubmitDateRange([null, null]);
    setTimeToSubmitDateRangeOption([]);
    setEnableTimeToSubmitCustomRange(false);
    setTimeToSubmitDateFilterOpen(false);
  };

  // Handle time to submit dropdown open/close
  const handleOpenTimeToSubmitDateFilterDropdown = (open: boolean) => {
    if (
      !open &&
      (!timeToSubmitDateRangeOption ||
        (Array.isArray(timeToSubmitDateRangeOption) &&
          timeToSubmitDateRangeOption.length === 0))
    ) {
      setTimeToSubmitDateRangeOption([]);
    }
    setTimeToSubmitDateFilterOpen(open);
  };

  // Page size preference for Captured Submissions table
  // Default is 5 records if no preference is saved
  const pageSizeCapturedSubmissions = handlePreferences(PreferenceActions.GET, {
    name: PAGE_SIZE_CAPTURED_SUBMISSIONS,
  });
  const capturedSubmissionsPageSizeValue =
    pageSizeCapturedSubmissions &&
    typeof pageSizeCapturedSubmissions === 'number'
      ? pageSizeCapturedSubmissions
      : 5; // Default: 5 records

  const saveCapturedSubmissionsPageSize = (pageSize: number) => {
    if (currentUser) {
      handlePreferences(PreferenceActions.SET, {
        name: PAGE_SIZE_CAPTURED_SUBMISSIONS,
        value: pageSize,
      });
    }
  };

  // Calculate time to submit metrics for each team member
  const timeToSubmitMetrics = React.useMemo(() => {
    const userMetrics: {
      [key: string]: {
        name: string;
        avgDaysToSubmit: number;
        totalSubmissions: number;
        fastestSubmission: number | null;
        slowestSubmission: number | null;
        submissionsThisMonth: number;
        submissionsThisWeek: number;
      };
    } = {};

    filteredTimeToSubmitLogs.forEach((log: VatReturnLog) => {
      if (!log.generatedDate || !log.submissionDate) return;

      const generatedDate = dayjs(log.generatedDate);
      const submissionDate = dayjs(log.submissionDate);

      if (!generatedDate.isValid() || !submissionDate.isValid()) return;

      const daysToSubmit = submissionDate.diff(generatedDate, 'day');

      // Get users involved (preparer, reviewer, or savedBy)
      const users: string[] = [];
      if (log.preparerUser) {
        const preparerName =
          `${log.preparerUser.firstName || ''} ${
            log.preparerUser.lastName || ''
          }`.trim() ||
          log.preparerUser.email ||
          'Unknown';
        users.push(preparerName);
      }
      if (log.reviewerUser) {
        const reviewerName =
          `${log.reviewerUser.firstName || ''} ${
            log.reviewerUser.lastName || ''
          }`.trim() ||
          log.reviewerUser.email ||
          'Unknown';
        users.push(reviewerName);
      }
      if (log.savedByUserFullName) {
        users.push(log.savedByUserFullName);
      }

      users.forEach((userName) => {
        if (!userMetrics[userName]) {
          userMetrics[userName] = {
            name: userName,
            avgDaysToSubmit: 0,
            totalSubmissions: 0,
            fastestSubmission: null,
            slowestSubmission: null,
            submissionsThisMonth: 0,
            submissionsThisWeek: 0,
          };
        }

        const metrics = userMetrics[userName];
        metrics.totalSubmissions += 1;
        metrics.avgDaysToSubmit =
          (metrics.avgDaysToSubmit * (metrics.totalSubmissions - 1) +
            daysToSubmit) /
          metrics.totalSubmissions;

        if (
          metrics.fastestSubmission === null ||
          daysToSubmit < metrics.fastestSubmission
        ) {
          metrics.fastestSubmission = daysToSubmit;
        }
        if (
          metrics.slowestSubmission === null ||
          daysToSubmit > metrics.slowestSubmission
        ) {
          metrics.slowestSubmission = daysToSubmit;
        }

        // Check if submission is this month
        const today = dayjs();
        const thisMonthStart = today.startOf('month');
        const thisMonthEnd = today.startOf('month').add(1, 'month');
        if (
          submissionDate.isValid() &&
          submissionDate >= thisMonthStart &&
          submissionDate < thisMonthEnd
        ) {
          metrics.submissionsThisMonth += 1;
        }

        // Check if submission is this week
        const thisWeekStart = today.startOf('week');
        const thisWeekEnd = today.endOf('week').add(1, 'day');
        if (
          submissionDate.isValid() &&
          submissionDate >= thisWeekStart &&
          submissionDate < thisWeekEnd
        ) {
          metrics.submissionsThisWeek += 1;
        }
      });
    });

    return Object.values(userMetrics).sort(
      (a, b) => b.totalSubmissions - a.totalSubmissions
    );
  }, [filteredTimeToSubmitLogs]);

  return (
    <React.Fragment>
      <Helmet>
        <title>{`${t('navigation.home')} | ${process.env.PROJECT_NAME}`}</title>
        <meta
          name="description"
          content={
            pwaMobileNav
              ? t('organisation.pwa-home-select-entity-subtitle')
              : t('dashboard.page-meta-description')
          }
        />
      </Helmet>
      {pwaMobileNav ? (
        <PwaHomeEntityList
          entities={entities}
          pinnedEntityIds={pinnedEntityIds}
          loading={isLoading}
        />
      ) : (
        <HomeComponent
          entities={entities}
          mappedReturns={mappedReturns}
          currentUser={currentUser}
          accountUsers={accountUsers}
          handlePreferences={handlePreferences}
          dashboardViewType={dashboardViewType}
          isLoading={isLoading}
          setDashboardViewType={setDashboardViewType}
          isManager={isManager}
          cardOrder={cardOrder}
          saveCardOrder={saveCardOrder}
          visibleCards={visibleCards}
          addCard={addCard}
          removeCard={removeCard}
          saveCardVisibilityBatch={saveCardVisibilityBatch}
          resetVisibleCardsToDefault={React.useCallback(() => {
            // CRITICAL: On reset, clear card visibility preference to restore all default cards
            // This ensures that previously hidden cards are shown again after reset
            const defaultOrder = getDefaultCardOrderForView(dashboardViewType);
            // Explicitly clear the visibility preference by setting it to null
            handlePreferences(PreferenceActions.SET, {
              name: getCardVisibilityPreferenceKey(dashboardViewType),
              value: null,
            });
            // Also clear the card order preference to ensure it's reset
            handlePreferences(PreferenceActions.SET, {
              name: getCardOrderPreferenceKey(dashboardViewType),
              value: null,
            });
            // Update state to show all default cards
            setVisibleCards(defaultOrder);
            // Update cardOrder state to match
            setCardOrder(defaultOrder);
          }, [
            dashboardViewType,
            getDefaultCardOrderForView,
            handlePreferences,
            getCardVisibilityPreferenceKey,
            getCardOrderPreferenceKey,
          ])}
          cardHeights={cardHeights}
          saveCardHeights={saveCardHeights}
          cardWidths={cardWidths}
          saveCardWidths={saveCardWidths}
          cardLayouts={cardLayouts}
          saveCardLayouts={saveCardLayouts}
          showOnlyMyReturns={showOnlyMyReturns}
          setShowOnlyMyReturns={setShowOnlyMyReturns}
          submittedDateRange={submittedDateRange}
          submittedDateRangeOption={submittedDateRangeOption}
          enableCustomRange={enableCustomRange}
          submittedReturnsData={submittedReturnsData}
          submittedDateFilterOpen={submittedDateFilterOpen}
          filteredSubmittedReturns={filteredSubmittedReturns}
          filterKey={filterKey}
          selectedUserNames={selectedUserNames}
          tempSelectedUserNames={tempSelectedUserNames}
          teamAllocationFilterOpen={teamAllocationFilterOpen}
          hoveredTeamAllocation={hoveredTeamAllocation}
          setHoveredTeamAllocation={setHoveredTeamAllocation}
          submittedThisMonth={submittedThisMonth}
          captureDashboardStats={captureDashboardStats}
          notStartedCount={notStartedCount}
          pendingTasks={pendingTasks}
          awaitingReview={awaitingReview}
          savedTasks={savedTasks}
          awaitingClientTasks={awaitingClientTasks}
          reviewerApprovedTasks={reviewerApprovedTasks}
          awaitingApprovalTasks={awaitingApprovalTasks}
          approvedTasks={approvedTasks}
          clientApprovedTasks={clientApprovedTasks}
          lockedTasks={lockedTasks}
          submittedTasks={submittedTasks}
          cancelledTasks={cancelledTasks}
          notSavedTasks={notSavedTasks}
          exceptionsModifiedTasks={exceptionsModifiedTasks}
          notStartedTasks={notStartedTasks}
          overdueReturns={overdueReturns}
          dueTodayReturns={dueTodayReturns}
          dueThisWeekReturns={dueThisWeekReturns}
          dueThisMonthReturns={dueThisMonthReturns}
          dueNextMonthReturns={dueNextMonthReturns}
          dueLaterReturns={dueLaterReturns}
          preparerCount={preparerCount}
          reviewerCount={reviewerCount}
          maxRoleValue={maxRoleValue}
          returnTypeCounts={returnTypeCounts}
          maxReturnTypeValue={maxReturnTypeValue}
          userData={userData}
          filteredUserData={filteredUserData}
          handleStatusClick={handleStatusClick}
          handleDueDateClick={handleDueDateClick}
          handleReturnTypeClick={handleReturnTypeClick}
          handleRoleClick={handleRoleClick}
          handlePersonClick={handlePersonClick}
          navigateToSubmissions={navigateToSubmissions}
          openSubmission={openSubmission}
          createSubmission={createSubmission}
          onSubmittedDateOptionSelect={onSubmittedDateOptionSelect}
          onCustomRangeCheckboxChange={onCustomRangeCheckboxChange}
          handleApplyDateFilter={handleApplyDateFilter}
          handleCancelDateFilter={handleCancelDateFilter}
          handleOpenDateFilterDropdown={handleOpenDateFilterDropdown}
          setSubmittedDateRange={setSubmittedDateRange}
          setSelectedUserNames={setSelectedUserNames}
          setTempSelectedUserNames={setTempSelectedUserNames}
          setTeamAllocationFilterOpen={setTeamAllocationFilterOpen}
          TEAM_ALLOCATION_USER_FILTER={TEAM_ALLOCATION_USER_FILTER}
          capturedSubmissionsPageSize={capturedSubmissionsPageSizeValue}
          saveCapturedSubmissionsPageSize={saveCapturedSubmissionsPageSize}
          handleTotalEntitiesClick={handleTotalEntitiesClick}
          handleTotalReturnsClick={handleTotalReturnsClick}
          handleNotStartedClick={handleNotStartedClick}
          handlePendingTasksClick={handlePendingTasksClick}
          handleAwaitingReviewClick={handleAwaitingReviewClick}
          handleSubmittedThisMonthClick={handleSubmittedThisMonthClick}
          timeToSubmitDateRange={timeToSubmitDateRange}
          timeToSubmitDateRangeOption={timeToSubmitDateRangeOption}
          enableTimeToSubmitCustomRange={enableTimeToSubmitCustomRange}
          timeToSubmitDateFilterOpen={timeToSubmitDateFilterOpen}
          timeToSubmitMetrics={timeToSubmitMetrics}
          onTimeToSubmitDateOptionSelect={onTimeToSubmitDateOptionSelect}
          onTimeToSubmitCustomRangeCheckboxChange={
            onTimeToSubmitCustomRangeCheckboxChange
          }
          handleApplyTimeToSubmitDateFilter={handleApplyTimeToSubmitDateFilter}
          handleCancelTimeToSubmitDateFilter={
            handleCancelTimeToSubmitDateFilter
          }
          handleOpenTimeToSubmitDateFilterDropdown={
            handleOpenTimeToSubmitDateFilterDropdown
          }
          setTimeToSubmitDateRange={setTimeToSubmitDateRange}
        />
      )}
    </React.Fragment>
  );
};
